/**
 * Utility functions for annotations
 */

import { GRID_LEVEL_THRESHOLDS, GRID_LEVELS, SUBDIVISIONS_PER_BEAT, getSubdivisionsForMeasure } from './constants';

/**
 * Calculate the number of beats visible in the current viewport
 */
export const calculateVisibleBeats = (viewportWidth, zoomLevel, pixelsPerBeat) => {
  const actualPixelsPerBeat = pixelsPerBeat * zoomLevel;
  return Math.ceil(viewportWidth / actualPixelsPerBeat);
};

/**
 * Determine the appropriate grid level based on zoom and visible beats
 */
export const getGridLevel = (zoomLevel, visibleBeats) => {
  // Highest priority: If zoomed in enough, show subdivisions
  if (zoomLevel >= GRID_LEVEL_THRESHOLDS.SUBDIVISION_ZOOM_MIN) {
    return GRID_LEVELS.SUBDIVISION;
  }
  
  // Medium priority: If fewer than 80 beats visible, show beats
  if (visibleBeats < GRID_LEVEL_THRESHOLDS.BEAT_VISIBILITY_MAX) {
    return GRID_LEVELS.BEAT;
  }
  
  // Lowest priority: If 80+ beats visible, show only bars
  return GRID_LEVELS.BAR;
};

/**
 * Convert pixel position to musical position with grid level awareness
 */

// resizing & creating block should pass doProximity as true
export const toMusicalPositionWithGridLevel = (x, gridLevel, doProximity, beatsPerMeasureMap, pixelsPerBeat, timeSignatures) => {
  // If no beatsPerMeasureMap, return default values
  if (!beatsPerMeasureMap || beatsPerMeasureMap.length === 0) {
    return { measure: 1, beat: 1, subdivision: 1 };
  }

  let totalPixels = 0;

  // Find which measure contains this x position
  for (let i = 0; i < beatsPerMeasureMap.length; i++) {
    const measureBeats = beatsPerMeasureMap[i];
    const measureWidth = beatsPerMeasureMap[i] * pixelsPerBeat;

    if (x < totalPixels + measureWidth) {
      if (gridLevel === GRID_LEVELS.BAR && doProximity) {
        return x <= totalPixels + measureWidth / 2 || i === beatsPerMeasureMap.length - 1 ? {
          measure: i + 1,
          beat: 1,
          subdivision: 1
        } : {
          measure: i + 2,
          beat: 1,
          subdivision: 1
        };
      }
      // Found the measure, now calculate the beat
      const relativeXInMeasure = x - totalPixels;
      const beatFloat = relativeXInMeasure / pixelsPerBeat;
      let beat = Math.floor(beatFloat) + 1;

      // Ensure beat doesn't exceed the number of beats in this measure
      // If it does, we should advance to the next measure
      if (beat > measureBeats) {
        if (i < beatsPerMeasureMap.length - 1) {
          // Not the last measure, advance to next measure
          return {
            measure: i + 2, // because 0-based index
            beat: 1,
            subdivision: 1
          };
        } else {
          // This is the last measure, position at N+1
          return {
            measure: beatsPerMeasureMap.length + 1,
            beat: 1,
            subdivision: 1
          };
        }
      }

      if (gridLevel === GRID_LEVELS.BEAT && doProximity) {
        // proximity checks
        const beatIndexLo = Math.floor(beatFloat), beatIndexHi = Math.ceil(beatFloat);
        const beatIndex = beatFloat - beatIndexLo <= beatIndexHi - beatFloat ? beatIndexLo : beatIndexHi;
        const beat = beatIndex + 1; // 1 based index

        if (beatIndex + 1 > measureBeats && i < beatsPerMeasureMap.length - 1) { // shift to next measure/bar
          return {
            measure: i + 2, // because 0 index
            beat: 1,
            subdivision: 1
          };
        } else {
          return {
            measure: i + 1,
            beat: beat,
            subdivision: 1
          };
        }
      }

      // Subdivision level, always do proximity check, because for annotation drag we will readjust using width anyways
      // and for resize and create we will want proximity check (if at subdivision level)
      // Get subdivisions per beat for this measure

      const subdivisionsPerBeat = timeSignatures ?
        getSubdivisionsForMeasure(timeSignatures, i) :
        SUBDIVISIONS_PER_BEAT;

      const relativeXInBeat = x - totalPixels - pixelsPerBeat * (beat - 1);
      const subdivFloat = relativeXInBeat / pixelsPerBeat * subdivisionsPerBeat;
      const subdivIndexLo = Math.floor(subdivFloat), subdivIndexHi = Math.ceil(subdivFloat);
      const subdivIndex = subdivFloat - subdivIndexLo <= subdivIndexHi - subdivFloat ? subdivIndexLo : subdivIndexHi;
      const subdision = subdivIndex + 1; // 1 based index

      if (subdision > subdivisionsPerBeat) { // shift to next measure/bar
        beat++;
        if (beat > measureBeats) { // overflow twice
          if (i < beatsPerMeasureMap.length - 1) {
            // Not the last measure, advance to next measure
            return {
              measure: i + 2, // because 0 index
              beat: 1,
              subdivision: 1
            };
          } else {
            // This is the last measure, position at N+1
            return {
              measure: beatsPerMeasureMap.length + 1,
              beat: 1,
              subdivision: 1
            };
          }
        } else {
          return {
            measure: i + 1,
            beat: beat,
            subdivision: 1
          };
        }
      }

      return {
        measure: i + 1,
        beat: beat,
        subdivision: subdision
      };
    }

    totalPixels += measureWidth;
  }

  // If we're past the last measure, allow positioning at the N+1 bar position
  // This represents the end of the last subdivision of the last beat of the last bar
  const distancePastEnd = x - totalPixels;
  const extraSpaceThreshold = pixelsPerBeat * 0.1; // Allow positions within the extra space we added
  
  // Be more generous with N+1 positioning - if we're anywhere in the extra space region,
  // treat it as the N+1 position. This ensures blocks extending to the rightmost edge
  // display the correct end time (e.g., 121.1.1 instead of 120.5.1)
  if (distancePastEnd >= 0 && distancePastEnd <= extraSpaceThreshold) {
    // Allow positioning at the N+1 bar (end of last subdivision)
    const lastMeasureBeats = beatsPerMeasureMap[beatsPerMeasureMap.length - 1];
    const lastSubdivisionsPerBeat = timeSignatures ?
      getSubdivisionsForMeasure(timeSignatures, beatsPerMeasureMap.length - 1) :
      SUBDIVISIONS_PER_BEAT;
    
    return {
      measure: beatsPerMeasureMap.length + 1,  // N+1 position
      beat: 1,
      subdivision: 1
    };
  }

  // If we're way past the extra space, return the last beat of the last measure
  return {
    measure: beatsPerMeasureMap.length,
    beat: beatsPerMeasureMap[beatsPerMeasureMap.length - 1],
    subdivision: 1
  };
};

export const computeMinWidth = (measure, beat, subdivision, beatsPerMeasureMap, pixelsPerBeat, timeSignatures, gridLevel) => {
  let totalPixels = 0;
  const x = calculatePosition(measure, beat, subdivision, beatsPerMeasureMap, pixelsPerBeat, timeSignatures);

  // Find which measure contains this x position
  for (let i = 0; i < beatsPerMeasureMap.length; i++) {
    const measureBeats = beatsPerMeasureMap[i];
    const measureWidth = beatsPerMeasureMap[i] * pixelsPerBeat;
    const subdivisionsPerBeat = timeSignatures ?
      getSubdivisionsForMeasure(timeSignatures, i) :
      SUBDIVISIONS_PER_BEAT;

    if (x < totalPixels + measureWidth) {
      if (gridLevel === GRID_LEVELS.BAR) {
        return measureWidth;
      } else if (gridLevel === GRID_LEVELS.BEAT) {
        return pixelsPerBeat;
      } else {
        return pixelsPerBeat / subdivisionsPerBeat
      }
    }
    totalPixels += measureWidth;
  }
  return null;
}

/**
 * Calculate position for a given measure, beat, and subdivision
 */
export const calculatePosition = (measure, beat, subdivision = 1, beatsPerMeasureMap, pixelsPerBeat, timeSignatures = null) => {
  let position = 0;
  
  // Handle N+1 bar position (end of last subdivision of last bar)
  if (measure > beatsPerMeasureMap.length) {
    // Calculate position at the end of all measures
    for (let i = 0; i < beatsPerMeasureMap.length; i++) {
      position += beatsPerMeasureMap[i] * pixelsPerBeat;
    }
    return position; // This is the N+1 bar position
  }
  
  // Add up all beats in previous measures
  for (let i = 1; i < Math.min(measure, beatsPerMeasureMap.length); i++) {
    position += beatsPerMeasureMap[i-1] * pixelsPerBeat;
  }
  
  // Add beats in current measure
  if (beat > 1) {
    position += (beat - 1) * pixelsPerBeat;
  }
  
  // Add subdivision (adjust subdivision to be 0-based for calculation)
  // In music notation, subdivision starts at 1, but we need 0-based for calculation
  const internalSubdivision = subdivision - 1;
  if (internalSubdivision >= 0) {
    // Get subdivisions per beat for this measure
    const subdivisionsPerBeat = timeSignatures ? 
      getSubdivisionsForMeasure(timeSignatures, measure - 1) : 
      SUBDIVISIONS_PER_BEAT;
    
    if (subdivisionsPerBeat > 0) {
      position += (internalSubdivision / subdivisionsPerBeat) * pixelsPerBeat;
    }
  }
  if (isNaN(position)) {
    console.error("calculatePosition should not be NaN but it is, params:", measure, beat, subdivision, beatsPerMeasureMap, pixelsPerBeat, timeSignatures);
  }
  
  return position;
};

/**
 * Calculate total width based on measures and beats per measure
 */
export const calculateTotalWidth = (measures, beatsPerMeasureMap, pixelsPerBeat) => {
  let totalWidth = 0;
  if (!beatsPerMeasureMap || beatsPerMeasureMap.length === 0) {
    return totalWidth; // No measures, no width
  }
  for (let i = 0; i < measures; i++) {
    totalWidth += beatsPerMeasureMap[i] * pixelsPerBeat;
  }
  // Add extra space to show the N+1 bar line (end of last subdivision of last bar)
  // This provides a visual and functional boundary for the rightmost position
  totalWidth += pixelsPerBeat * 0.1; // Small additional space for the N+1 bar line
  return totalWidth;
};

/**
 * Check for musical overlap between two annotations
 */
export const checkMusicalOverlap = (
  newStartMeasure, newStartBeat, newStartSubdivision,
  newEndMeasure, newEndBeat, newEndSubdivision,
  otherStartMeasure, otherStartBeat, otherStartSubdivision,
  otherEndMeasure, otherEndBeat, otherEndSubdivision
) => {
  // Ensure subdivision values are numbers
  newStartSubdivision = newStartSubdivision || 0;
  newEndSubdivision = newEndSubdivision || 0;
  otherStartSubdivision = otherStartSubdivision || 0;
  otherEndSubdivision = otherEndSubdivision || 0;

  // Check if the annotations would overlap in musical time
  return (
    // New annotation starts inside other annotation
    ((newStartMeasure > otherStartMeasure || 
      (newStartMeasure === otherStartMeasure && 
       (newStartBeat > otherStartBeat || 
        (newStartBeat === otherStartBeat && 
         newStartSubdivision >= otherStartSubdivision)))) &&
     (newStartMeasure < otherEndMeasure || 
      (newStartMeasure === otherEndMeasure && 
       (newStartBeat < otherEndBeat || 
        (newStartBeat === otherEndBeat && 
         newStartSubdivision < otherEndSubdivision))))) ||
    // New annotation ends inside other annotation
    ((newEndMeasure > otherStartMeasure || 
      (newEndMeasure === otherStartMeasure && 
       (newEndBeat > otherStartBeat || 
        (newEndBeat === otherStartBeat && 
         newEndSubdivision > otherStartSubdivision)))) &&
     (newEndMeasure < otherEndMeasure || 
      (newEndMeasure === otherEndMeasure && 
       (newEndBeat < otherEndBeat || 
        (newEndBeat === otherEndBeat && 
         newEndSubdivision <= otherEndSubdivision))))) ||
    // New annotation completely contains other annotation
    ((newStartMeasure < otherStartMeasure || 
      (newStartMeasure === otherStartMeasure && 
       (newStartBeat < otherStartBeat || 
        (newStartBeat === otherStartBeat && 
         newStartSubdivision <= otherStartSubdivision)))) &&
     (newEndMeasure > otherEndMeasure || 
      (newEndMeasure === otherEndMeasure && 
       (newEndBeat > otherEndBeat || 
        (newEndBeat === otherEndBeat && 
         newEndSubdivision >= otherEndSubdivision)))))
  );
};

/**
 * Get display text for an annotation
 */
export const getAnnotationDisplayText = (annotation) => {
  return `${annotation.formData?.title || 'Annotation'} (${annotation.startMeasure}.${annotation.startBeat}.${annotation.startSubdivision || 1} - ${annotation.endMeasure}.${annotation.endBeat}.${annotation.endSubdivision || 1})`;
};

/**
 * Check if position A is before position B in musical time
 */
export const isBeforeInTime = (
  measureA, beatA, subdivisionA,
  measureB, beatB, subdivisionB
) => {
  return measureA < measureB || 
    (measureA === measureB && 
      (beatA < beatB || 
        (beatA === beatB && 
          (subdivisionA || 0) < (subdivisionB || 0))));
};

/**
 * Check if position A is after position B in musical time
 */
export const isAfterInTime = (
  measureA, beatA, subdivisionA,
  measureB, beatB, subdivisionB
) => {
  return measureA > measureB || 
    (measureA === measureB && 
      (beatA > beatB || 
        (beatA === beatB && 
          (subdivisionA || 0) > (subdivisionB || 0))));
};

/**
 * Check if an annotation can be created or moved to a position
 */
export const canCreateOrMoveAnnotation = (
  startMeasure, startBeat, startSubdivision,
  endMeasure, endBeat, endSubdivision,
  otherAnnotations
) => {
  for (const other of otherAnnotations) {
    const otherStartSubdivision = other.startSubdivision || 0;
    const otherEndSubdivision = other.endSubdivision || 0;
    
    if (checkMusicalOverlap(
      startMeasure, startBeat, startSubdivision,
      endMeasure, endBeat, endSubdivision,
      other.startMeasure, other.startBeat, otherStartSubdivision,
      other.endMeasure, other.endBeat, otherEndSubdivision
    )) {
      return false;
    }
  }
  return true;
};

/**
 * Find the closest non-overlapping end position
 */
export const findLeftRightAnnotationBoundaries = (x, annotations, calculatePosition) => {
  let closestLeft = -Infinity;
  let closestRight = Infinity;

  for (const annotation of annotations) {
    const startX = calculatePosition(annotation.startMeasure, annotation.startBeat, annotation.startSubdivision || 0);
    const endX = calculatePosition(annotation.endMeasure, annotation.endBeat, annotation.endSubdivision || 0);

    if (endX <= x && endX > closestLeft) {
      closestLeft = endX;
    }
    if (startX >= x && startX < closestRight) {
      closestRight = startX;
    }
  }
  return { closestLeft, closestRight };
};
